import { createFetch, type Fn } from "@vueuse/core";
import { refreshCsrfToken } from "~vue/utils/csrf";
import { logError } from "~vue/utils/logError";
import { generateRequestId, REQUEST_ID_HEADER } from "~vue/utils/requestId";
import { reportSessionEnded, SESSION_ENDED_ERROR_CAUSE, sessionEndedCodeFrom, wasRecoveryDeclined } from "~vue/utils/sessionEnded";
import { isAlbWafBlock, WAF_ERROR_CAUSE } from "~vue/utils/wafDetection";

import { getCookie } from "../../utils.js";
import jwtToken from "./token.js";

const VERSIONED_API_PATH_REGEX = /^\/v\d+\//;
// Rater-facing reflection endpoints that work without auth (external 360 raters).
// Exported for installSessionEndedFetch, which must not offer a login to a rater who has no
// account -- their 403 means a bad invite, not a session that ended.
export const OPTIONAL_AUTH_PATH_REGEX = /^\/v\d+\/reflections\/(invites\/|responses\/)/;
const { apiToken, isFetchingToken, tokenPromise, getToken } = jwtToken;

const _ensureToken = async () => {
    if (isFetchingToken.value && tokenPromise.value) {
        await tokenPromise.value;
    } else {
        await getToken();
    }
};

const _tokenHandler = async (options: RequestInit, cancel: Fn, url: string) => {
    try {
        await _ensureToken();
    } catch {
        // For rater-facing endpoints, proceed without auth (external raters).
        if (!OPTIONAL_AUTH_PATH_REGEX.test(url)) {
            cancel();
        }
    }

    // once the token is fetched, it needs to be added to headers
    if (apiToken.value) {
        options.headers = {
            ...(options.headers as Record<string, string>),
            [REQUEST_ID_HEADER]: generateRequestId(),
            Authorization: `Bearer ${apiToken.value}`,
        };
    } else {
        options.headers = {
            ...(options.headers as Record<string, string>),
            [REQUEST_ID_HEADER]: generateRequestId(),
        };
    }
};

/**
 * Plain fetch wrapper for coach_api. Handles token acquisition automatically.
 * Use this instead of raw fetch() when calling coach_api from non-composable code.
 *
 * @param path - A versioned API path, e.g. "/v1/events/ux"
 * @param init - Standard fetch RequestInit options
 */
export const fetchApi = async (path: string, init: RequestInit = {}): Promise<Response> => {
    await _ensureToken();

    return fetch(path, {
        ...init,
        headers: {
            ...(init.headers as Record<string, string> | undefined),
            "Content-Type": "application/json",
            [REQUEST_ID_HEADER]: generateRequestId(),
            ...(apiToken.value ? { Authorization: `Bearer ${apiToken.value}` } : {}),
        },
    });
};

/**
 * Pull a human-readable error message out of a coach_api or Django error body.
 *
 * coach_api returns {"error", "code"} (ENG-1155); Django and some legacy
 * endpoints use {"detail"}, {"error_message"}, or a bare string array. Checked
 * in priority order so one helper covers every shape.
 *
 * "detail" is a transitional compatibility branch for the pre-ENG-1155
 * coach_api format. TODO(ENG-1600): remove it once the {error, code} envelope
 * is deployed and stable in both production regions. "error_message" and the
 * string-array shape are Django's and stay until vpoc errors are standardized.
 *
 * @internal Exported for unit tests only. Components should not import this:
 * useFetch already runs it and stores the result in `error.value.message`,
 * which callers read directly (`error.value?.message || "<fallback>"`).
 */
export function extractApiErrorMessage(data: unknown, fallback = ""): string {
    if (Array.isArray(data)) {
        return typeof data[0] === "string" && data[0] ? data[0] : fallback;
    }
    if (data && typeof data === "object") {
        const body = data as Record<string, unknown>;
        // TODO(ENG-1600): drop "detail" from this list.
        for (const key of ["error", "detail", "error_message"] as const) {
            const value = body[key];
            if (typeof value === "string" && value) {
                return value;
            }
        }
    }
    return fallback;
}

export const useFetch = createFetch({
    combination: "chain",
    options: {
        updateDataOnError: true, // Ensure that the data is updated even on error
        beforeFetch: async ({ options, url, cancel }) => {
            if (VERSIONED_API_PATH_REGEX.test(url)) {
                // Coach API: attach JWT token (URL stays same-origin relative)
                await _tokenHandler(options, cancel, url);
                return { options, url };
            } else if (options.method !== "GET") {
                // Django API: add CSRF token for non-GET requests.
                let cookie = getCookie("csrftoken");
                if (!cookie) {
                    cookie = await refreshCsrfToken();
                }
                options.headers = {
                    ...(options.headers as Record<string, string>),
                    [REQUEST_ID_HEADER]: generateRequestId(),
                    // Marks this as a caller that can act on a JSON 403 rather than needing a
                    // rendered page. _wants_machine_response keys on Sec-Fetch-Mode
                    // first, which browsers always send, so this is the documented fallback for a
                    // client that sends none rather than the primary signal.
                    "X-Requested-With": "XMLHttpRequest",
                    ...(cookie ? { "X-CSRFToken": cookie } : {}),
                };
            } else {
                // Django API: GET requests
                options.headers = {
                    ...(options.headers as Record<string, string>),
                    [REQUEST_ID_HEADER]: generateRequestId(),
                    "X-Requested-With": "XMLHttpRequest",
                };
            }
        },
        onFetchError(ctx) {
            // A backstop. installSessionEndedFetch reports these first, and a recoverable stale
            // token never reaches this hook at all because the retry it performs returns a 2xx.
            // What lands here is the unrecoverable case, plus any body the wrapper could not
            // parse. reportSessionEnded is first-report-wins, so a duplicate is harmless.
            //
            // Runs before the WAF check because that one keys off a header this response does not
            // carry, and before the message rewriting because the dialog is the better remedy.
            // The caller's own onFetchError event still fires and may render an inline error
            // underneath the dialog; that is per-component and out of scope here.
            const status = ctx.response?.status;
            // Unless the wrapper already looked at this exact response and chose not to report it.
            // That only happens when the token refresh was unavailable (5xx, dropped connection),
            // where the session may well be alive: reporting anyway would open the modal the
            // wrapper declined to open, and the reload behind it costs the user unsaved work.
            // The wrapper decides, this defers -- and only for the 403s it acts on, so the 401
            // arm and the unparsable-body handling below are untouched.
            const sessionEndedCode = (status === 403 || status === 401) && !wasRecoveryDeclined(ctx.response) ? sessionEndedCodeFrom(ctx.data) : null;
            if (sessionEndedCode) {
                reportSessionEnded(sessionEndedCode, (ctx.data as { login_url?: string })?.login_url ?? null);
                // Tagged rather than cleared: the caller still has to unwind whatever it was doing,
                // it just must not render a second message about it (ENG-1667).
                const handled = new Error("Session ended", { cause: SESSION_ENDED_ERROR_CAUSE });
                handled.name = "SessionEndedError";
                ctx.error = handled;
                return ctx;
            }
            // See isAlbWafBlock for the WAF/ALB-vs-app-403 detection rule (mirrors StreamingPostClient's gating).
            if (isAlbWafBlock(ctx.response)) {
                const replacement = new Error("Your request was blocked by our security system. Please try again.", { cause: WAF_ERROR_CAUSE });
                replacement.name = "WafBlockError";
                ctx.error = replacement;
                logError(ctx.error, {
                    context: { fetch: { status: ctx.response?.status, statusText: ctx.response?.statusText } },
                    tags: { error_type: "waf_block" },
                });
                return ctx;
            }
            // vueuse's useFetch parses the response body BEFORE checking response.ok,
            // so when the server returns a non-JSON body (e.g. an HTML 500 page),
            // the original error is a confusing SyntaxError from JSON.parse rather
            // than the real underlying HTTP failure. Replace it with a clear,
            // user-facing message that still groups cleanly in Sentry (by name).
            if (ctx.error instanceof SyntaxError) {
                let message = "We couldn't complete that request. Please try again.";
                if (status === 401 || status === 403) {
                    message = "Couldn't verify your session. Please refresh the page and try again.";
                } else if (status && status >= 500) {
                    message = "Something went wrong on our end. Please try again in a moment.";
                }
                const replacement = new Error(message);
                replacement.name = "NonJsonResponseError";
                ctx.error = replacement;
            }
            // Pull the server's message out of the error body. coach_api now
            // returns {"error", "code"} (ENG-1155); Django/legacy endpoints use
            // {"detail"}, {"error_message"}, or a bare string array. Previously
            // {"detail"} was not handled, so HTTPException messages were dropped.
            // TODO(ENG-1600): {"detail"} support is transitional; see extractApiErrorMessage.
            else {
                const message = extractApiErrorMessage(ctx.data);
                if (message) {
                    ctx.error.message = message;
                }
            }
            // "Failed to fetch" TypeError is expected when users navigate away or have network issues.
            if (ctx.error instanceof TypeError && ctx.error.message === "Failed to fetch") {
                return ctx;
            }
            // no_speech_detected is an expected user condition, not a bug — skip error logging.
            if (ctx.data?.error_code === "no_speech_detected") {
                return ctx;
            }
            // Strip query params to avoid logging sensitive data (tokens, etc.)
            let urlPath: string | undefined;
            try {
                urlPath = ctx.response?.url ? new URL(ctx.response.url).pathname : undefined;
            } catch {
                urlPath = undefined;
            }
            logError(ctx.error, {
                context: {
                    fetch: {
                        status: ctx.response?.status,
                        statusText: ctx.response?.statusText,
                        url: urlPath,
                    },
                },
            });
            return ctx;
        },
    },
});
